import React, { useState } from "react";
import { StatusBar, BottomNav } from "../components/Shell";
import Ico from "../components/Ico";
import { useAppStore } from "../components/AppStore";
import { weekDays, calendarTasks } from "../mock";
import { CalendarDays, Target, CheckCircle2, DollarSign, Plus, Store } from "lucide-react";

export default function CalendarScreen() {
  const [sel, setSel] = useState(18);
  const tasks = calendarTasks[String(sel)] || [];
  const { postedJobs } = useAppStore();
  const scheduled = postedJobs.filter((j) => j.status === "parent_approved");

  return (
    <div className="gd-screen bg-[#eef1fb]">
      <div className="flex-shrink-0 px-5 pb-5" style={{ background: "linear-gradient(160deg,#3b57c9,#2b3f96)", borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
        <StatusBar dark={false} />
        <div className="mt-1 flex items-start justify-between">
          <div><h1 className="text-2xl font-extrabold text-white">My Calendar</h1><p className="text-sm text-white/70">3 tasks this week</p></div>
          <span className="flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold text-white"><CalendarDays size={14} /> June 2025</span>
        </div>
        <div className="mt-4 flex justify-between">
          {weekDays.map((d) => (
            <button key={d.n} onClick={() => setSel(d.n)} className="flex flex-col items-center gap-1.5 rounded-2xl px-2.5 py-2 transition-all"
              style={{ background: sel === d.n ? "#fff" : "rgba(255,255,255,.12)" }}>
              <span className="text-[10px] font-bold" style={{ color: sel === d.n ? "#8b93b5" : "rgba(255,255,255,.7)" }}>{d.d}</span>
              <span className="text-sm font-extrabold" style={{ color: sel === d.n ? "#2b3f96" : "#fff" }}>{d.n}</span>
              {d.active && <span className="h-1 w-1 rounded-full" style={{ background: sel === d.n ? "#f5911e" : "#fff" }} />}
            </button>
          ))}
        </div>
      </div>

      <div className="gd-scroll px-4 py-4 gd-stagger">
        <div className="grid grid-cols-3 gap-3">
          <CalStat icon={Target} tint="#3b57c9" v="3" l="Upcoming" />
          <CalStat icon={CheckCircle2} tint="#4fae3f" v="12" l="Done" />
          <CalStat icon={DollarSign} tint="#f5911e" v="$143" l="Earned" />
        </div>

        <div className="mb-2 mt-5 flex items-center justify-between">
          <h3 className="text-base font-extrabold text-[#1f2a52]">Wednesday, Jun {sel}</h3>
          <button className="gd-tap flex items-center gap-1 rounded-full bg-[#3b57c9] px-3 py-1.5 text-xs font-bold text-white"><Plus size={13} /> Add Task</button>
        </div>

        {tasks.length === 0 ? (
          <div className="mt-14 flex flex-col items-center text-center text-[#9aa3c0]">
            <CalendarDays size={40} className="mb-3 text-[#c7cde6]" />
            <p className="text-sm font-semibold">No tasks scheduled</p>
            <p className="mt-1 text-xs">Accept a chore to auto-fill your calendar.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tasks.map((t, i) => (
              <div key={`${t.time}-${t.title}`} className="gd-card flex items-center gap-3 overflow-hidden p-3" style={{ borderLeft: `5px solid ${t.tint}` }}>
                <div className="w-14 flex-shrink-0 text-center">
                  <p className="text-xs font-extrabold text-[#1f2a52]">{t.time.split(" ")[0]}</p>
                  <p className="text-[9px] font-bold text-[#9aa3c0]">{t.time.split(" ")[1]}</p>
                </div>
                <div className="flex-1 border-l border-[#f0f2fa] pl-3">
                  <p className="text-sm font-extrabold text-[#1f2a52]">{t.title}</p>
                  <p className="text-[11px] font-semibold text-[#9aa3c0]"><span className="text-[#4fae3f]">${t.pay}</span> · {t.dur}</p>
                </div>
                <span className="rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: t.status === "Done" ? "#eaf6ea" : "#eef2ff", color: t.status === "Done" ? "#3d8f34" : "#3b57c9" }}>{t.status}</span>
              </div>
            ))}
          </div>
        )}

        {/* approved neighbor jobs (synced from parent approval) */}
        {scheduled.length > 0 && (
          <>
            <div className="mb-2 mt-6 flex items-center gap-2">
              <Store size={15} className="text-[#f5911e]" />
              <h3 className="text-base font-extrabold text-[#1f2a52]">Neighbor Jobs — Scheduled</h3>
            </div>
            <div className="space-y-3">
              {scheduled.map((j) => (
                <div key={j.id} className="gd-card flex items-center gap-3 p-3" style={{ borderLeft: `5px solid ${j.tint}` }}>
                  <div className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: `${j.tint}18` }}><Ico name={j.icon} size={19} color={j.tint} /></div>
                  <div className="flex-1"><p className="text-sm font-extrabold text-[#1f2a52]">{j.title}</p><p className="text-[11px] font-semibold text-[#9aa3c0]">{j.schedule}</p></div>
                  <div className="text-right"><p className="text-sm font-black text-[#4fae3f]">${j.pay}</p><span className="text-[9px] font-bold text-[#3d8f34]">Approved</span></div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

function CalStat({ icon: Icon, tint, v, l }) {
  return (
    <div className="flex flex-col items-center rounded-[18px] bg-white py-3 shadow-[0_8px_20px_rgba(31,42,82,0.05)]">
      <Icon size={18} color={tint} />
      <span className="mt-1 text-xl font-black text-[#1f2a52]">{v}</span>
      <span className="text-[10px] font-semibold text-[#9aa3c0]">{l}</span>
    </div>
  );
}
